/**
 * Mountain (2026-09-22): misty ridges fading into the sky one behind another,
 * the flat layered landscape. Every ridge is a ridged 1D fbm (four octaves of (1 - |n|)^2 on a
 * seeded lattice, so the peaks are sharp and the valleys round) closed to the bottom edge;
 * aerial perspective mixes each ridge toward the horizon by its depth and mist pools in every
 * valley. All vector, no per pixel pass, so it is one of the cheapest styles at 5K.
 */
import { Rgb, Scene, WallpaperStyleInfo, share, rgba, mix, darken, lighten, luminance } from '../wallpaper-core';

export const INFO: WallpaperStyleInfo = {
  key: 'mountain', label: 'Mountain',
  blurb: 'Misty mountain ridges fading into the sky, layer behind layer',
  params: [ share( 'peaks', 'Peaks', 55 ), share( 'haze', 'Haze', 45 ) ],
};

/**
 * A seeded 1D lattice of values in -1..1 read with cosine interpolation, `cells` per box width.
 * It runs from u = -4 to u = 6 (the box is 0..1), wide enough for an ultrawide canvas and for the
 * peak shift below; the index is clamped rather than wrapped, since a wrap would seam the ridge.
 */
function lattice( rand: () => number, cells: number ): ( u: number ) => number {
  const n = Math.ceil( cells * 10 ) + 2;
  const v = new Float32Array( n );
  for ( let i = 0; i < n; i++ ) v[ i ] = rand() * 2 - 1;
  return ( u: number ) => {
    const x = Math.min( n - 1.000001, Math.max( 0, ( u + 4 ) * cells ) );
    const i = Math.floor( x );
    const k = ( 1 - Math.cos( ( x - i ) * Math.PI ) ) / 2;
    return v[ i ] + ( v[ i + 1 ] - v[ i ] ) * k;
  };
}

/**
 * The ridge line: four octaves of the folded lattice, (1 - |n|)^2, cells doubling and weight
 * falling by `gain`. The fold puts a sharp crest on every zero crossing and a round floor on
 * every extreme, which is what makes rock. `sharp` blends toward the unfolded value so low
 * Peaks give rolling hills instead of small saw teeth.
 */
function ridgeProfile( rand: () => number, baseCells: number, gain: number, sharp: number ): ( u: number ) => number {
  const layers: { n: ( u: number ) => number; a: number }[] = [];
  let cells = baseCells, amp = 1, total = 0;
  for ( let o = 0; o < 4; o++ ) {
    layers.push( { n: lattice( rand, cells ), a: amp } );
    total += amp;
    cells *= 2;
    amp *= gain;
  }
  return ( u: number ) => {
    let s = 0, prev = 1;
    for ( const l of layers ) {
      const n = l.n( u );
      const f = 1 - Math.abs( n );
      const fold = f * f;
      const soft = ( n + 1 ) / 2;
      // the small octaves weigh more on the crests than in the floors, so the peaks are rocky and
      // the valleys stay calm
      s += ( soft + ( fold - soft ) * sharp ) * l.a * ( 0.35 + 0.65 * prev );
      prev = fold;
    }
    return s / total;
  };
}

export function draw( scene: Scene ): void {
  const { ctx, w, h, box, colors, rand, p } = scene;
  const white: Rgb = { r: 255, g: 255, b: 255 };
  const px = box.w / 1920;
  // the tiles' order: the first tile is the sky and the mist tint, the rest are the ridges walked
  // back to front; two tiles split the one ridge tile into three ridges told apart by haze alone
  const sky = colors[ 0 ];
  const tiles = colors.length > 1 ? colors.slice( 1 ) : [ darken( sky, 0.45 ) ];
  const ridgeTiles = tiles.length >= 2 ? tiles : [ tiles[ 0 ], tiles[ 0 ], darken( tiles[ 0 ], 0.25 ) ];
  const n = ridgeTiles.length;
  // the horizon leans toward the farthest ridge so the haze has something to fade into; with two
  // tiles that would be the only ridge colour, so it leans toward white instead
  const horizon = tiles.length >= 2 ? mix( sky, tiles[ 0 ], 0.2 ) : lighten( sky, 0.12 );
  const zenith = darken( sky, 0.12 );
  const hLum = luminance( horizon );
  const dark = luminance( sky ) < 0.55;

  // baselines from 42% (back) to 88% (front) of the box; many tiles spread over more height so
  // nine ranges do not crowd the lower half
  const spread = Math.min( 0.12, 0.025 * Math.max( 0, n - 4 ) );
  const backBase = 0.42 - spread;
  const frontBase = 0.88;

  // Haze: how far the back dissolves into the sky; a pale sky caps it, since mixing toward a
  // near white horizon turns a ridge to mud before it turns to mist
  const hazeCap = 0.9 - 0.4 * hLum;
  const haze = Math.pow( p[ 1 ], 0.75 ) * hazeCap;
  const mistA = 0.45 * Math.pow( p[ 1 ], 0.8 );
  const mistSpan = 0.25 + 0.3 * p[ 1 ];
  // night mist still catches the sky's own light, so on a dark sky the veil is lifted off it;
  // on a pale one the sky is already the brightest thing in the frame
  const mistCol = dark ? lighten( horizon, 0.16 ) : horizon;
  // a ridge lighter than everything in front of it would invert the scene (a bright foreground
  // under a night sky): each ridge's tone is capped by a ceiling falling toward the front, never
  // past half so the tile stays recognisable
  const lMax = Math.max( hLum, ...ridgeTiles.map( luminance ) );
  // the tone a too light tile is pulled toward: the sky's own dark, so a pale ramp keeps its hue
  // instead of going grey the way a mix toward black does
  const shade = darken( sky, 0.45 );

  // the geometry of every range, back to front
  const steps = Math.ceil( ( 240 + 240 * p[ 0 ] ) * w / box.w );
  const ranges: { col: Rgb; baseY: number; crestY: number; pts: number[] }[] = [];
  for ( let i = 0; i < n; i++ ) {
    const f = n > 1 ? i / ( n - 1 ) : 1;
    const depth = 1 - f;
    const tile = ridgeTiles[ i ];
    const ceiling = lMax * ( 1 - 0.5 * f );
    const toned = luminance( tile ) > ceiling ? mix( tile, shade, Math.min( 0.55, 1 - ceiling / luminance( tile ) ) ) : tile;
    const col = mix( toned, horizon, haze * depth );
    const baseFrac = backBase + ( frontBase - backBase ) * f;
    const baseY = box.y + box.h * baseFrac;
    // Peaks: the amplitude and how much the small octaves add; the front ranges are lower so the
    // back ones show over them, and no range may reach the top 8% of the box, or tall Peaks with
    // ten tiles would roof the sky over
    const amp = Math.min( box.h * ( baseFrac - 0.08 ), box.h * ( 0.05 + 0.28 * p[ 0 ] ) * ( 1 - 0.32 * f ) * ( 0.9 + rand() * 0.2 ) );
    // distance also makes the peaks smaller and more of them: the near range gets a few broad
    // summits, the far one a tight saw of them, which is most of what reads as depth
    const cells = ( 2.7 + rand() * 1.2 ) * ( 1 + 1.3 * depth );
    const profile = ridgeProfile( rand, cells, 0.42 + 0.22 * p[ 0 ], 0.6 + 0.4 * p[ 0 ] );
    // the tallest peak is moved into the middle 26% of the box so the phone crop never shows only
    // foothills: the whole line is shifted, so its shape is untouched
    const target = 0.38 + rand() * 0.24;
    let uMax = 0, hMax = -1;
    for ( let s = 0; s <= 1500; s++ ) {
      const u = -1 + s * 0.002;
      const v = profile( u );
      if ( v > hMax ) { hMax = v; uMax = u; }
    }
    const shift = uMax - target;
    // only the y of each sample is kept: the x is the step, the same for every range
    const pts: number[] = [];
    let crestY = h;
    for ( let s = 0; s <= steps; s++ ) {
      const u = ( ( s / steps ) * w - box.x ) / box.w;
      const y = baseY - amp * profile( u + shift ) / hMax;
      if ( y < crestY ) crestY = y;
      pts.push( y );
    }
    ranges.push( { col, baseY, crestY, pts } );
  }

  // the sky: dark at the zenith, the horizon colour at the back baseline and below it
  const skyG = ctx.createLinearGradient( 0, 0, 0, box.y + box.h * backBase );
  skyG.addColorStop( 0, rgba( zenith, 1 ) );
  skyG.addColorStop( 1, rgba( horizon, 1 ) );
  ctx.fillStyle = skyG;
  ctx.fillRect( 0, 0, w, h );

  // the ranges, back to front, each closed to the bottom edge as one filled polygon
  let behind = horizon;
  for ( const r of ranges ) {
    ctx.beginPath();
    ctx.moveTo( 0, r.pts[ 0 ] );
    for ( let s = 1; s <= steps; s++ ) ctx.lineTo( ( s / steps ) * w, r.pts[ s ] );
    ctx.lineTo( w, h );
    ctx.lineTo( 0, h );
    ctx.closePath();
    // the body: the ridge colour at its crest, a 35% mix toward the ridge behind at its base, so
    // the layers separate without a hard strip; below the base it keeps sinking, which gives the
    // near ranges the weight a flat fill never has
    const foot = mix( r.col, behind, 0.35 );
    const body = ctx.createLinearGradient( 0, r.crestY, 0, h );
    body.addColorStop( 0, rgba( r.col, 1 ) );
    body.addColorStop( Math.min( 0.999, ( r.baseY - r.crestY ) / ( h - r.crestY ) ), rgba( foot, 1 ) );
    body.addColorStop( 1, rgba( darken( foot, 0.2 ), 1 ) );
    ctx.fillStyle = body;
    ctx.fill();
    // the mist: the sky colour rising from the base over the lower part of the ridge, so it pools
    // in every valley the next range leaves open
    if ( mistA > 0.001 ) {
      const mist = ctx.createLinearGradient( 0, r.baseY - ( r.baseY - r.crestY ) * mistSpan, 0, r.baseY );
      mist.addColorStop( 0, rgba( mistCol, 0 ) );
      mist.addColorStop( 1, rgba( mistCol, mistA ) );
      ctx.fillStyle = mist;
      ctx.fill();
    }
    behind = r.col;
  }

  // snow light on the nearest peaks: a hairline of white along the front crest
  const front = ranges[ n - 1 ].pts;
  ctx.beginPath();
  ctx.moveTo( 0, front[ 0 ] );
  for ( let s = 1; s <= steps; s++ ) ctx.lineTo( ( s / steps ) * w, front[ s ] );
  ctx.strokeStyle = rgba( white, 0.2 );
  ctx.lineWidth = Math.max( 1, px );
  ctx.lineJoin = 'round';
  ctx.stroke();
}
